package ai.wagemix.web.screens.pricing

import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.pricing.WMDefaultPricingPresenter
import ai.wagemix.logic.pricing.WMPricingAccess
import ai.wagemix.logic.pricing.WMPricingPresentation
import ai.wagemix.logic.pricing.WMPricingPresenter
import ai.wagemix.logic.pricing.WMPricingPresenter.Action
import ai.wagemix.logic.pricing.WMPricingPresenter.Event
import ai.wagemix.logic.pricing.WMPricingPresenter.Prompt
import ai.wagemix.net.models.WMPlanPrice
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMFaqItem
import ai.wagemix.web.components.WMFormat
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMPlan
import ai.wagemix.web.components.WMPlanCard
import ai.wagemix.web.components.WMPlanFeature
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.screens.planName
import ai.wagemix.web.theme.WMCardGridGapVar
import ai.wagemix.web.theme.WMLeadTextStyle
import ai.wagemix.web.theme.WMPlanCardWidthVar
import ai.wagemix.web.theme.WMSectionContentGapVar
import ai.wagemix.web.theme.WMSectionTitleTextStyle
import androidx.compose.runtime.Composable
import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMLabelTextStyle
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.padding
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import ai.wagemix.web.screens.WMPromptDialog
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px

interface WMPricingWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val pricingState: State<WMPricingPresentation?>
}

abstract class WMBasePricingWebViewModel<P: Prompt, E: Event<P>, A: Action, PR: WMPricingPresenter<P, E, A>>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMPricingWebViewStateHolder<P> {

    override val pricingState: State<WMPricingPresentation?> get() = _pricingState
    protected val _pricingState = mutableStateOf<WMPricingPresentation?>(null)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            pricingUpdate?.let { _pricingState.value = it }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userRequestedCheckout(lookupKey: String) = presenter.userRequestedCheckout(lookupKey)
    fun userRequestedResendVerification() = presenter.userRequestedResendVerification()
}

typealias WMDefaultPricingWebViewModel = WMBasePricingWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultPricingPresenter>

fun createDefaultPricingWebViewModel(
    presenter: WMDefaultPricingPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit
): WMDefaultPricingWebViewModel = object: WMDefaultPricingWebViewModel(presenter, externalNavigation) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}

val WMPlanGridStyle = CssStyle.base {
    Modifier.fillMaxWidth().display(DisplayStyle.Grid)
        .gridTemplateColumns { repeat(autoFit) { minmax(WMPlanCardWidthVar.value(), 1.fr) } }
        .gap(WMCardGridGapVar.value())
}

@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMPricingPresenter<P, E, A>, VM: WMBasePricingWebViewModel<P, E, A, PR>> WMPricingScreen(
    createViewModel: () -> VM,
    strings: WMWebStrings,
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel) { vm ->
        WMPricingScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            pricingState = vm.pricingState,
            modifier = modifier,
            onChoose = { lookupKey -> vm.userRequestedCheckout(lookupKey) }
        )
        val prompt = vm.promptState.value
        val respond = { i: Int -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, i)) }
        val pr = strings.pricing
        WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = respond) { p ->
            when (p) {
                is Prompt.SignInToSubscribe -> WMDialog(
                    title = pr.signInTitle, description = pr.signInMessage,
                    onDismiss = { respond(Prompt.SignInToSubscribe.Selection.Dismiss.ordinal) },
                    actions = {
                        WMButton(strings.common.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.SignInToSubscribe.Selection.Dismiss.ordinal) }
                        WMButton(strings.common.signIn, kind = WMButtonKind.Secondary, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.SignInToSubscribe.Selection.SignIn.ordinal) }
                        WMButton(strings.common.register, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.SignInToSubscribe.Selection.Register.ordinal) }
                    }
                ) {}
                is Prompt.VerifyEmailFirst -> WMDialog(
                    title = pr.verifyTitle, description = "${pr.verifyMessage} (${p.email})",
                    onDismiss = { respond(Prompt.VerifyEmailFirst.Selection.Dismiss.ordinal) },
                    actions = {
                        WMButton(strings.common.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.VerifyEmailFirst.Selection.Dismiss.ordinal) }
                        WMButton(pr.resend, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.VerifyEmailFirst.Selection.Resend.ordinal) }
                    }
                ) {}
                is Prompt.VerificationSent -> WMDialog(
                    title = pr.sentTitle, description = "${pr.sentMessage} (${p.email})",
                    onDismiss = { respond(0) },
                    actions = { WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) { respond(0) } }
                ) {}
                else -> Unit
            }
        }
    }
}

/** The Stripe recurring interval of the plans Wagemix sells. */
private const val YEARLY_INTERVAL = "year"

@Composable
fun WMPricingScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    pricingState: State<WMPricingPresentation?>,
    modifier: Modifier = Modifier,
    onChoose: (lookupKey: String) -> Unit
) {
    val pr = strings.pricing
    val colors = ColorMode.current.toPalette().fs
    val pricing = pricingState.value

    // Wagemix sells yearly plans only; a price on any other interval is never offered.
    fun priceOf(plan: WMSubscriptionType): WMPlanPrice? = pricing?.prices?.firstOrNull { it.plan == plan && it.interval == YEARLY_INTERVAL }
    fun priceText(price: WMPlanPrice?): String = price?.let { WMFormat.money((it.unitAmount / 100).toInt()) } ?: "—"

    // Penpot `Pricing`: the plans, the comparison and the questions share one width and start at the left edge.
    Column(modifier = Modifier.fillMaxWidth().gap(WMSectionContentGapVar.value()).then(modifier), horizontalAlignment = Alignment.Start) {
        SpanText(pr.title, WMSectionTitleTextStyle.toModifier())
        SpanText(pr.subtitle, WMLeadTextStyle.toModifier().color(colors.onSurfaceVariant).maxWidth(720.px))

        val current = pricing?.currentPlan ?: WMSubscriptionType.None
        // A lapsed account's own plan takes the highlight and a renew button, in place of the most popular plan.
        val renewPlan = pricing?.renewPlan
        if (renewPlan != null) {
            val title = pr.renewNoticeTitle(strings.planName(renewPlan))
            WMNotice(title, kind = WMNoticeKind.Warning, message = pr.renewNoticeMessage)
        }
        val highlighted = renewPlan ?: WMSubscriptionType.Basic
        val highlightLabel = if (renewPlan != null) pr.yourPlan else pr.popular
        fun chooseLabel(plan: WMSubscriptionType): String = when (plan) {
            current -> pr.currentPlan
            renewPlan -> strings.renewal.renewPlan(strings.planName(plan))
            else -> pr.choose
        }
        fun chooseKind(plan: WMSubscriptionType): WMButtonKind {
            return if (plan == highlighted) WMButtonKind.Primary else WMButtonKind.Secondary
        }
        Div(WMPlanGridStyle.toModifier().toAttrs()) {
            val freeIsCurrent = pricing?.freeIsCurrent == true
            WMPlanCard(WMPlan(name = strings.common.free, priceText = "$0", priceCaption = "", description = pr.freeDescription, features = pr.freeFeatures.map { WMPlanFeature(it) }, current = freeIsCurrent)) {
                WMButton(if (freeIsCurrent) pr.currentPlan else pr.startFree, kind = WMButtonKind.Secondary, fullWidth = true, enabled = pricing?.canStartFree == true) { kotlinx.browser.window.location.href = ai.wagemix.logic.WMRoutes.REGISTER }
            }
            val basic = priceOf(WMSubscriptionType.Basic)
            val basicHighlighted = highlighted == WMSubscriptionType.Basic
            WMPlanCard(WMPlan(name = strings.common.basic, priceText = priceText(basic), priceCaption = pr.perYear, description = pr.basicDescription, features = pr.basicFeatures.map { WMPlanFeature(it) }, highlighted = basicHighlighted, highlightLabel = highlightLabel.takeIf { basicHighlighted }, current = current == WMSubscriptionType.Basic)) {
                WMButton(chooseLabel(WMSubscriptionType.Basic), kind = chooseKind(WMSubscriptionType.Basic), fullWidth = true, enabled = basic != null && current != WMSubscriptionType.Basic && !loadingState.value) { basic?.let { onChoose(it.lookupKey) } }
            }
            val pro = priceOf(WMSubscriptionType.Pro)
            val proHighlighted = highlighted == WMSubscriptionType.Pro
            WMPlanCard(WMPlan(name = strings.common.pro, priceText = priceText(pro), priceCaption = pr.perYear, description = pr.proDescription, features = pr.proFeatures.map { WMPlanFeature(it) }, highlighted = proHighlighted, highlightLabel = highlightLabel.takeIf { proHighlighted }, current = current == WMSubscriptionType.Pro)) {
                WMButton(chooseLabel(WMSubscriptionType.Pro), kind = chooseKind(WMSubscriptionType.Pro), fullWidth = true, enabled = pro != null && current != WMSubscriptionType.Pro && !loadingState.value) { pro?.let { onChoose(it.lookupKey) } }
            }
        }

        Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
            SpanText(pr.compareTitle, WMSectionTitleTextStyle.toModifier())
            Table(attrs = Modifier.fillMaxWidth().toAttrs()) {
                Thead {
                    Tr {
                        Th(attrs = Modifier.textAlign(TextAlign.Left).padding(8.px).toAttrs()) { SpanText(pr.featureColumn, WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant)) }
                        listOf(strings.common.free, strings.common.basic, strings.common.pro).forEach { plan ->
                            Th(attrs = Modifier.textAlign(TextAlign.Center).padding(8.px).toAttrs()) { SpanText(plan, WMLabelStrongTextStyle.toModifier()) }
                        }
                    }
                }
                Tbody {
                    pr.comparison.forEach { (feature, cells) ->
                        Tr {
                            Td(attrs = Modifier.padding(8.px).borderTop(1.px, LineStyle.Solid, colors.outlineVariant).toAttrs()) { SpanText(feature, WMBodyTextStyle.toModifier()) }
                            cells.forEach { cell ->
                                Td(attrs = Modifier.padding(8.px).textAlign(TextAlign.Center).borderTop(1.px, LineStyle.Solid, colors.outlineVariant).toAttrs()) {
                                    SpanText(cell.ifEmpty { "—" }, WMBodyTextStyle.toModifier().color(if (cell.isEmpty()) colors.outline else colors.onSurface))
                                }
                            }
                        }
                    }
                }
            }
        }

        Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
            SpanText(pr.faqTitle, WMSectionTitleTextStyle.toModifier())
            pr.faq.forEach { (q, a) -> WMFaqItem(q, a) }
        }
    }
}
